import {
  ThemeProvider as EmotionThemeProvider,
  type Theme,
} from '@emotion/react';
import type { UserPreferenceTheme } from '@quromedical/models';
import { logger, noOp } from 'helpers';
import { useAsyncStorageData } from 'hooks/useAsyncStorageData';
import type React from 'react';
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useColorScheme } from 'react-native';

import { darkTheme, darkThemeHighContrast, lightTheme } from './color';

interface ThemeProviderProps {
  theme: UserPreferenceTheme;
}

interface ThemeStateContextData {
  theme: UserPreferenceTheme;
  changeTheme: (theme: UserPreferenceTheme) => void;
}

const ThemeStateContext = createContext<ThemeStateContextData>({
  theme: 'dark',
  changeTheme: noOp,
});

export const useThemeStateContext = () => useContext(ThemeStateContext);

export const ThemeProvider: React.FC<
  React.PropsWithChildren<ThemeProviderProps>
> = ({ children, theme }) => {
  const colorScheme = useColorScheme() || 'dark';

  const isDark = colorScheme === 'dark';

  const systemTheme = isDark ? darkTheme : lightTheme;

  const themeMap: Record<UserPreferenceTheme, Theme> = {
    light: lightTheme,
    dark: darkTheme,
    system: systemTheme,
    'contrast-dark': darkThemeHighContrast,
  };

  const resolvedTheme = themeMap[theme];

  return (
    <EmotionThemeProvider theme={resolvedTheme}>
      {children}
    </EmotionThemeProvider>
  );
};

export const ThemeState: React.FC<PropsWithChildren> = ({ children }) => {
  const [theme, setTheme] = useState<UserPreferenceTheme>('dark');
  const themeStore = useAsyncStorageData<UserPreferenceTheme>(
    'userpreference.theme',
    'dark',
  );

  useEffect(() => {
    themeStore.getData().then(setTheme).catch(logger.error);
  }, [themeStore]);

  const changeTheme = (value: UserPreferenceTheme = 'dark') => {
    setTheme(value);
    themeStore.setData(value).catch(logger.error);
  };

  return (
    <ThemeStateContext.Provider value={{ theme, changeTheme }}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeStateContext.Provider>
  );
};
